import ApiClient from '@afosto/api/ApiClient';

class Index {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Create a new index
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create a new index
   * @returns {Promise}
   */
  createInstantIndex(body, options = {}) {
    return this.apiClient.request({
      url: `/cnt/instant/indexes`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Decode filter
   *
   * @param {string} input
   * @param {Object} options - The additional request options
   * @description Decode a relative date filter
   * @returns {Promise}
   */
  decodeDateFilter(input, options = {}) {
    const params = {
      input,
    };

    return this.apiClient.request({
      url: `/cnt/filters/date`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Delete a single index
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Delete a single index
   * @returns {Promise}
   */
  deleteInstantIndex(id, options = {}) {
    return this.apiClient.request({
      url: `/cnt/instant/indexes/${id}`,
      method: 'delete',
      ...options,
    });
  }

  /**
   * Relative date
   *
   * @param {Object} body - Query object
   * @param {Object} options - The additional request options
   * @description Generate a relative date filter
   * @returns {Promise}
   */
  generateDateFilter(body, options = {}) {
    return this.apiClient.request({
      url: `/cnt/filters/date`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Show a single index
   * 
   * @param {string} id 
   * @param {Object} options - The additional request options
   * @description Show a single index for the tenant.
  
   * @returns {Promise}
   */
  getInstantIndex(id, options = {}) {
    return this.apiClient.request({
      url: `/cnt/instant/indexes/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of definitions
   *
   * @param {Object} query - The optional path parameters
   * @param {string} [query.code]
   * @param {string} [query.model]
   * @param {string} [query.namespace]
   * @param {Object} options - The additional request options
   * @description List the set of definitions
   * @returns {Promise}
   */
  listDefinitions(query = {}, options = {}) {
    const params = {
      code: query.code,
      model: query.model,
      namespace: query.namespace,
    };

    return this.apiClient.request({
      url: `/cnt/definitions`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * List all indexes
   * 
   * @param {Object} options - The additional request options
   * @description List all indexes for the authenticated user.
  
   * @returns {Promise}
   */
  listInstantIndexes(options = {}) {
    return this.apiClient.request({
      url: `/cnt/instant/indexes`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Run a simple search
   *
   * @param {Object} body - Query object
   * @param {Object} options - The additional request options
   * @description Run a search with the option to run an aggregation
   * @returns {Promise}
   */
  search(body, options = {}) {
    return this.apiClient.request({
      url: `/cnt/search`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Run a complex search
   *
   * @param {string} code
   * @param {string} model
   * @param {string} namespace - the namespace
   * @param {Object} body - Query object
   * @param {Object} options - The additional request options
   * @description Run a complex search
   * @returns {Promise}
   */
  searchModel(namespace, code, model, body, options = {}) {
    return this.apiClient.request({
      url: `/cnt/search/${namespace}/${code}/${model}`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Get some suggestions
   *
   * @param {string} q - The query
   * @param {Object} query - The optional path parameters
   * @param {array} [query.codes] - The query
   * @param {Number} [query.limit] - The query
   * @param {array} [query.models] - The models we want to query
   * @param {array} [query.namespaces] - The namesapce we want to query
   * @param {Object} options - The additional request options
   * @description Run a simple, fast suggest search
   * @returns {Promise}
   */
  suggest(q, query = {}, options = {}) {
    const params = {
      codes: query.codes,
      limit: query.limit,
      models: query.models,
      namespaces: query.namespaces,
      q,
    };

    return this.apiClient.request({
      url: `/cnt/suggest`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Update a single index
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Update a single index
   * @returns {Promise}
   */
  updateInstantIndex(id, body, options = {}) {
    return this.apiClient.request({
      url: `/cnt/instant/indexes/${id}`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Upsert a definition
   *
   * @param {Object} body - Query object
   * @param {Object} options - The additional request options
   * @description Either create a new, or update an existing definition
   * @returns {Promise}
   */
  upsertDefinition(body, options = {}) {
    return this.apiClient.request({
      url: `/cnt/definitions`,
      method: 'post',
      data: body,
      ...options,
    });
  }
}

export const index = new Index();

export default Index;
